<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <h4 class="card-title">Criar Novo Usuário</h4>
            <p class="card-title-desc">
              Preencha os dados abaixo para criar um novo usuário no sistema.
            </p>
            
            <form @submit.prevent="submitForm" class="form-horizontal" role="form">
              
              <!-- Informações Básicas -->
              <div class="mb-4">
                <h5 class="font-size-14 mb-4">Informações Básicas</h5>
                
                <div class="row">
                  <div class="col-md-6">
                    <b-form-group
                      id="form-email"
                      label="Email"
                      label-for="email"
                    >
                      <b-form-input
                        id="email"
                        v-model="form.email"
                        type="email"
                        placeholder="Digite o email do usuário"
                        :state="$v.form.email.$error ? false : null"
                        required
                      />
                      <b-form-invalid-feedback v-if="$v.form.email.$error">
                        <span v-if="!$v.form.email.required">Email é obrigatório</span>
                        <span v-if="!$v.form.email.email">Email deve ser válido</span>
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>

                  <div class="col-md-6">
                    <b-form-group
                      id="form-name"
                      label="Nome Completo"
                      label-for="name"
                    >
                      <b-form-input
                        id="name"
                        v-model="form.name"
                        type="text"
                        placeholder="Digite o nome completo do usuário"
                        :state="$v.form.name.$error ? false : null"
                        required
                      />
                      <b-form-invalid-feedback v-if="$v.form.name.$error">
                        <span v-if="!$v.form.name.required">Nome é obrigatório</span>
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <b-form-group
                      id="form-password"
                      label="Senha"
                      label-for="password"
                    >
                      <b-form-input
                        id="password"
                        v-model="form.password"
                        type="password"
                        placeholder="Digite a senha (mínimo 6 caracteres)"
                        :state="$v.form.password.$error ? false : null"
                        required
                      />
                      <b-form-invalid-feedback v-if="$v.form.password.$error">
                        <span v-if="!$v.form.password.required">Senha é obrigatória</span>
                        <span v-if="!$v.form.password.minLength">Senha deve ter pelo menos 6 caracteres</span>
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </div>

                  <div class="col-md-6">
                    <b-form-group
                      id="form-cpf"
                      label="CPF"
                      label-for="cpf"
                    >
                      <b-form-input
                        id="cpf"
                        v-model="form.cpf"
                        v-mask="'###.###.###-##'"
                        type="text"
                        placeholder="000.000.000-00"
                        maxlength="14"
                      />
                      <small class="form-text text-muted">
                        Opcional - Documento de identificação
                      </small>
                    </b-form-group>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12">
                    <b-form-group
                      id="form-role"
                      label="Tipo de Usuário"
                      label-for="role"
                    >
                      <select
                        id="role"
                        v-model="form.role"
                        class="form-control"
                        :class="{ 'is-invalid': $v.form.role.$error }"
                        required
                      >
                        <option value="">Selecione o tipo de usuário</option>
                        <option value="ADMIN">Administrador</option>
                        <option value="DOCTOR">Médico</option>
                        <option value="TECHNICIAN">Técnico</option>
                      </select>
                      <div v-if="$v.form.role.$error" class="invalid-feedback">
                        <span v-if="!$v.form.role.required">Tipo de usuário é obrigatório</span>
                      </div>
                    </b-form-group>
                  </div>
                </div>
              </div>

              <!-- Informações Profissionais (Médicos e Técnicos) -->
              <div v-if="form.role === 'DOCTOR' || form.role === 'TECHNICIAN'" class="mb-4">
                <h5 class="font-size-14 mb-4">Informações Profissionais</h5>
                
                <div class="row">
                  <div class="col-md-6">
                    <b-form-group
                      id="form-cbo"
                      label="CBO (Classificação Brasileira de Ocupações)"
                      label-for="cbo"
                    >
                      <b-form-input
                        id="cbo"
                        v-model="form.cbo"
                        v-mask="'######'"
                        type="text"
                        placeholder="225125"
                        maxlength="6"
                      />
                      <small class="form-text text-muted">
                        Código da ocupação profissional
                      </small>
                    </b-form-group>
                  </div>

                  <div class="col-md-6" v-if="form.role === 'DOCTOR'">
                    <b-form-group
                      id="form-crm"
                      label="CRM"
                      label-for="crm"
                    >
                      <b-form-input
                        id="crm"
                        v-model="form.crm"
                        v-mask="'######-AA'"
                        type="text"
                        placeholder="123456-SP"
                      />
                      <small class="form-text text-muted">
                        Registro no Conselho Regional de Medicina
                      </small>
                    </b-form-group>
                  </div>
                </div>

                <div class="row" v-if="form.role === 'DOCTOR'">
                  <div class="col-md-6">
                    <b-form-group
                      id="form-especialidade-codigo"
                      label="Código da Especialidade"
                      label-for="especialidade_codigo"
                    >
                      <b-form-input
                        id="especialidade_codigo"
                        v-model="form.especialidade_codigo"
                        v-mask="'##########'"
                        type="text"
                        placeholder="Ex: 01, 02, 03"
                        maxlength="10"
                      />
                      <small class="form-text text-muted">
                        Código oficial da especialidade
                      </small>
                    </b-form-group>
                  </div>

                  <div class="col-md-6">
                    <b-form-group
                      id="form-especialidade-nome"
                      label="Especialidade"
                      label-for="especialidade_nome"
                    >
                      <b-form-input
                        id="especialidade_nome"
                        v-model="form.especialidade_nome"
                        type="text"
                        placeholder="Ex: Cardiologia, Radiologia"
                      />
                      <small class="form-text text-muted">
                        Especialidade médica principal
                      </small>
                    </b-form-group>
                  </div>
                </div>
              </div>

              <!-- Permissões Especiais -->
              <div class="mb-4">
                <h5 class="font-size-14 mb-4">Permissões Especiais</h5>
                
                <div class="row">
                  <div class="col-md-12">
                    <div class="form-check">
                      <input
                        id="is_superuser"
                        v-model="form.is_superuser"
                        class="form-check-input"
                        type="checkbox"
                      />
                      <label class="form-check-label" for="is_superuser">
                        <strong>Super Usuário</strong> - Conceder acesso total ao sistema (recomendado apenas para administradores)
                      </label>
                    </div>
                    <small class="form-text text-muted mt-1">
                      Super usuários têm acesso irrestrito a todas as funcionalidades do sistema.
                    </small>
                  </div>
                </div>
              </div>

              <!-- Botões de Ação -->
              <div class="row">
                <div class="col-md-12 text-end">
                  <button type="button" class="btn btn-secondary me-2" @click="resetForm">
                    <i class="ri-refresh-line me-1"></i>
                    Limpar Formulário
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary ml-3"
                    :disabled="loading || $v.form.$invalid"
                  >
                    <i v-if="loading" class="bx bx-loader bx-spin me-1"></i>
                    <i v-else class="ri-user-add-line me-1"></i>
                    {{ loading ? 'Criando Usuário...' : 'Criar Usuário' }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { userService } from "@/api/users";
import { required, email, minLength } from "vuelidate/lib/validators";

export default {
  page: {
    title: "Criar Usuário",
    meta: [{ name: "description", content: "Criar novo usuário no sistema" }],
  },
  components: {
    Layout,
    PageHeader,
  },
  data() {
    return {
      title: "Criar Usuário",
      items: [
        {
          text: "Administração",
          href: "/admin",
        },
        {
          text: "Criar Usuário",
          active: true,
        },
      ],
      form: {
        email: "",
        password: "",
        name: "",
        role: "",
        is_superuser: false,
        cpf: "",
        cns: "",
        cbo: "",
        crm: "",
        especialidade_codigo: "",
        especialidade_nome: "",
      },
      loading: false,
    };
  },
  validations: {
    form: {
      email: { required, email },
      password: { required, minLength: minLength(6) },
      name: { required },
      role: { required },
    },
  },
  methods: {
    async submitForm() {
      this.$v.$touch();
      
      if (this.$v.$invalid) {
        this.$showError("Por favor, corrija os erros no formulário");
        return;
      }

      this.loading = true;
      try {
        // Preparar dados para envio - remover formatação dos campos
        const formData = {
          ...this.form,
          cpf: this.form.cpf ? this.form.cpf.replace(/\D/g, '') : '', // Remove pontos e hífen
          cbo: this.form.cbo ? this.form.cbo.replace(/\D/g, '') : '', // Remove não numéricos
          crm: this.form.crm ? this.form.crm : '', // CRM fica como está (contém letras)
          especialidade_codigo: this.form.especialidade_codigo ? this.form.especialidade_codigo.replace(/\D/g, '') : ''
        };
        
        await userService.createUser(formData);
        
        // Se chegou até aqui, foi bem-sucedido
        this.$showSuccess("Usuário criado com sucesso!");
        this.resetForm();
        
      } catch (error) {
        console.error("Erro ao criar usuário:", error);
        this.$handleApiError(error);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        email: "",
        password: "",
        name: "",
        role: "",
        is_superuser: false,
        cpf: "",
        cns: "",
        cbo: "",
        crm: "",
        especialidade_codigo: "",
        especialidade_nome: "",
      };
      this.$v.$reset();
    }    
  },
};
</script>
